/**
 * Wall footings + stem walls (FOOTINGS round, 2026-08-09 — docs/MODELER.md "WALL
 * FOOTINGS + STEM WALLS"): per-wall foundation, fully DERIVED — nothing stored but
 * intent (the wall's footingEnabled/footingTypeId/stemTypeId/stemHeightFt/stemMode
 * instance params). Emits plain PRISMS grouped like column prisms, so 3D extrusion,
 * section/elevation cut rects, vertical pick, cutaway, and spot elevations all fall
 * out of the existing machinery; section poché fills through the structural-prism
 * material branch (section.ts). `skipProjectedOutline` keeps every generic PLAN pass
 * off them — plan representation (dashed footing lines / a foundation-plan view type)
 * is the recorded follow-on, not this round.
 *
 * THE ALIGNMENT RULE (derived, never stored): the stem's OUTSIDE core face aligns
 * flush with the wall's OUTSIDE core face — the core always bears on the stem,
 * consistent to the outer face. With exterior = LEFT of draw direction (unflipped)
 * and a reference line at depth `refOff` from the exterior face, a line at depth `o`
 * sits at σ·(o − refOff) along the left normal (σ = flipped ? +1 : −1 — the
 * centerlineOffsetFt derivation, generalized). The stem extends INWARD from the
 * outer core face, so its axis offset is σ·(coreStart − refOff + stemW/2); the
 * footing centers under the stem axis. Changing the wall type re-derives the stack.
 *
 * TOPO MODE: the stem height derives from a grade profile along the run via the
 * footing type's coverage rule (min bury below finish grade to TOP of footing), WITH
 * STEPPING: as grade falls, the stem/footing discretize into steps at the type's
 * step increment — each step's footing top is quantized DOWN from the wall base
 * (zBase − k·step), so every step keeps coverage; step boundaries are bisected to
 * the grade crossing. A sample with no terrain under it (outside the hull, or no
 * topo element at all) falls back to the manual stemHeightFt — a wall with no site
 * behaves like manual mode. Multiple topo elements resolve first-non-null in element
 * order (v1 — realistically one terrain per model).
 *
 * TERRAIN DECOUPLE round (docs/bim-plan/19): WHERE the grade profile comes from
 * split in two. A wall carrying a CAPTURED `terrainFit` (the "Update to terrain"
 * action — see TerrainFit in bim-kernel types.ts for the doctrine note: a captured
 * fit is a SITE-CONDITION RECORD, what makes new-work regrading possible without
 * silently reshaping existing footings) reads its stored lanes and NEVER touches
 * the TIN — no terrain coupling at all, and docMirror's topo→footed-walls ripple
 * skips it. A wall with no capture reads the graded FINISH surface LIVE (every
 * modifier applied, unphased — the pre-round behavior, kept as the fallback so
 * capture-less walls are never wrong, just coupled). `sampleTerrainFit` below is
 * the ONE sampler shared by the live fallback, the capture action, and the survey
 * diff — capture-then-generate is bit-identical to the live generate at capture
 * time (the migration no-jump guarantee, pinned by test).
 */
import {
  isWoodCoreWall, referenceOffsets, offsetPolyline, PLATE_THICKNESS_IN, newNodeId, pathOps,
  assemblyThickness, centerlineOffsetFt, terrainFitOf, terrainFitLane,
  type Path2D, type Pt2, type ResolvedElement, type AssemblyRecord, type TerrainFit,
  type LocationLine,
} from '@aj/bim-kernel'
import { flattenPath } from '../../flatten'
import { clipperReady, differenceClosed, inflateOpen, intersectClosed } from '../../clipper'
import { placeOpening, wallFrame, type WallFrame } from '../../openings'
import {
  armFacePts, cornerPatchGeom, wallEndArms, CORNER_TOL, COLLINEAR_EPS, type CornerArm,
} from '../../cornerWeld'
import { gradedZAt, type ResolvedTopoModifier, type TopoTin } from '../topo/tin'
import { topoGradedFor } from '../topo/generate3d'
import { framingMaterialId, pointInRing } from '../roof/framing'
import { floorPlatformBand, type FloorPlatformBand } from '../floor/framing'
import type { ElementSolid } from './generate3d'

/** built-in fallbacks — match the footing/stem schema defaults exactly */
const FOOTING_DEFAULTS = { widthFt: 1.5, thicknessFt: 10 / 12, coverageDepthFt: 1, stepIncrementFt: 2, material: '' }
const STEM_DEFAULTS = { widthFt: 8 / 12, material: '' }

const SAMPLE_FT = 2         // topo-mode grade sampling step along the run
const MIN_RUN_FT = 0.05     // steps shorter than this merge into their neighbor
const EPS = 1e-6

/** total unsigned area of a ring set (shoelace) — the opening-cut miss test */
function areaOf(rings: Pt2[][]): number {
  let total = 0
  for (const r of rings) {
    let s = 0
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      s += r[j][0] * r[i][1] - r[i][0] * r[j][1]
    }
    total += Math.abs(s / 2)
  }
  return total
}

/** per-generate graded-surface memo: resolved topo elements are fresh objects every
 *  resolveAllElements pass, so a WeakMap keyed on them self-scopes to one generate —
 *  N footed walls over one terrain build its graded TIN once, never stale. */
const GRADED_MEMO = new WeakMap<ResolvedElement, { graded: TopoTin; existing: TopoTin; live: ResolvedTopoModifier[] } | null>()

function gradedFor(topo: ResolvedElement, byId?: Map<string, ResolvedElement>) {
  if (!GRADED_MEMO.has(topo)) GRADED_MEMO.set(topo, topoGradedFor(topo, byId))
  return GRADED_MEMO.get(topo) ?? null
}

/** FINISH SKIRT round (2026-08-16): the graded FINISH surface at a point — the
 *  same first-non-null-topo, per-generate-memoized read the foundation layout
 *  samples along the run, exported so the wall's finish skirt can hold its 4"
 *  clearance above the actual grade line. */
export function foundationGradeAt(
  byId: Map<string, ResolvedElement> | undefined, pt: Pt2,
): number | null {
  return liveGradeReader(byId)(pt)
}

/** the one first-non-null-topo LIVE grade read (per-generate memoized TINs) —
 *  shared by foundationGradeAt, the live topo-mode sampler, and sampleTerrainFit */
function liveGradeReader(byId?: Map<string, ResolvedElement>): (pt: Pt2) => number | null {
  if (!byId) return () => null
  const topos: ResolvedElement[] = []
  byId.forEach((e) => { if (e.category === 'topo') topos.push(e) })
  topos.sort((a, b) => (a.id < b.id ? -1 : 1))
  if (!topos.length) return () => null
  return (pt) => {
    for (const t of topos) {
      const g = gradedFor(t, byId)
      if (!g) continue
      const z = gradedZAt(g.existing, g.live, pt)
      if (z !== null) return z
    }
    return null
  }
}

interface FoundationRun {
  s0: number
  s1: number
  footingTop: number
  /** CURB round (2026-08-10): top of the stem — zBase when grade is below the wall
   *  base, stepped UP past it (grade + curb, quantized) where the base runs below
   *  grade, so the wall lands on a non-subterranean sill plate */
  stemTop: number
  /** FRAMING SESSION Phase 3 — FLOOR BEARING ON THE STEM (doctrine: BEARING
   *  INTENT IS DECLARED BY MODELED OVERLAP): set where a wood-framed floor's
   *  ring covers the stem AXIS — the stem top has derived DOWN to just under
   *  the floor framing (mudsill seat), a single MUDSILL plate rides on it, and
   *  the wall body/framing above start on the floor's SHEATHING (this value).
   *  A slab edge modeled to the stem's inner face never covers the axis — the
   *  geometric opt-out. */
  floorTop?: number
  /** true = emit the mudsill plate [stemTop, stemTop + plate] over this run */
  mudsill?: boolean
}

function num(v: unknown, fallback: number): number {
  return typeof v === 'number' && isFinite(v) ? v : fallback
}
function str(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback
}

/** A footed wall's derived foundation layout — shared by the prism emitter and the
 *  corner-weld post-pass (weldFoundationCornerSolids), memoized per generate the same
 *  way the graded TIN is. */
export interface FoundationLayout {
  axisPts: Pt2[]
  frame: WallFrame
  L: number
  runs: FoundationRun[]
  axisOffset: number
  stemW: number
  footW: number
  footT: number
  stemMat: string
  footMat: string
  /** STEM FINISH LAYERS round (2026-08-17): the stem type's own resolved
   *  assembly, when it has one — null for every stem type in a live model
   *  today. Exposed here (not recomputed by callers) so `stemW` above and any
   *  per-layer banding downstream always agree on the SAME resolved assembly. */
  stemAssembly: AssemblyRecord | null
  zBase: number
  /** ring/loop wall — no open ends, so never corner-welded */
  closed: boolean
}

const LAYOUT_MEMO = new WeakMap<ResolvedElement, FoundationLayout | null>()

export function foundationLayoutFor(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
): FoundationLayout | null {
  if (LAYOUT_MEMO.has(el)) return LAYOUT_MEMO.get(el) ?? null
  const layout = computeFoundationLayout(el, byId)
  LAYOUT_MEMO.set(el, layout)
  return layout
}

/** the geometric + type-param prelude shared by the layout computation and the
 *  terrain sampler — everything derivable from the element alone (no terrain) */
interface FoundationPrelude {
  path: Path2D
  footW: number
  footT: number
  coverage: number
  stepInc: number
  footMat: string
  stemAssembly: AssemblyRecord | null
  stemW: number
  stemMat: string
  axisOffset: number
  isLoop: boolean
  axisPath: Path2D
  axisPts: Pt2[]
  frame: WallFrame
  L: number
  zBase: number
  stemHeightManual: number
}

function foundationPrelude(el: ResolvedElement): FoundationPrelude | null {
  if (el.category !== 'wall' || !el.params.footingEnabled) return null
  if (!el.level || !clipperReady()) return null
  const path = el.params.path as Path2D | null
  if (!path || path.nodes.length < 2) return null

  const footP = el.paramTypes?.footingTypeId?.params
  const stemP = el.paramTypes?.stemTypeId?.params
  const footW = num(footP?.widthFt, FOOTING_DEFAULTS.widthFt)
  const footT = num(footP?.thicknessFt, FOOTING_DEFAULTS.thicknessFt)
  // COVERAGE (Matt, 2026-08-09 — a SITE condition, never a type param): the wall's
  // own nullable override, else the PROJECT structural setting (materialized onto
  // walls by resolveElement), else the built-in default
  const coverage = num(el.params.coverageDepthFt,
    num(el.structural?.coverageDepthFt, FOOTING_DEFAULTS.coverageDepthFt))
  const stepInc = Math.max(1 / 12, num(footP?.stepIncrementFt, FOOTING_DEFAULTS.stepIncrementFt))
  const footMat = str(footP?.material, FOOTING_DEFAULTS.material) || 'default'
  // STEM FINISH LAYERS round (2026-08-17): a stem type WITH an assembly uses its
  // real layer thickness sum as the true section width — the flat `widthFt` param
  // stays what a layer-less stem type reads (untouched, no migration needed).
  // Positioning (THE alignment rule's axisOffset below) and the inflate width
  // both read this SAME value, so the axis and the per-layer bands always agree.
  const stemAssembly = el.paramTypes?.stemTypeId?.assembly ?? null
  const stemW = stemAssembly && stemAssembly.layers.length
    ? assemblyThickness(stemAssembly)
    : num(stemP?.widthFt, STEM_DEFAULTS.widthFt)
  // `stemMat` still matters even for a layered stem type: it's the material a
  // corner-weld patch renders in (corner mitering stays single-material this
  // round — see weldFoundationCornerSolids below) and the fallback if per-layer
  // banding ever comes back empty for a run. Use the assembly's own CORE layer
  // rather than the flat (likely never-set) `material` param when one exists.
  const stemMat = stemAssembly && stemAssembly.layers.length
    ? (stemAssembly.layers[Math.min(Math.max(stemAssembly.coreBoundary?.start ?? 0, 0), stemAssembly.layers.length - 1)]
        ?.materialId || 'default')
    : (str(stemP?.material, STEM_DEFAULTS.material) || 'default')
  if (footW <= 0 || footT <= 0 || stemW <= 0) return null

  // THE alignment rule (header comment): stem axis = σ·(coreStart − refOff + stemW/2)
  // along the stored path's left normal. No assembly ⇒ offsets collapse to 0 and the
  // stem centers on the path — still a usable foundation under a zero-thickness wall.
  const offsets = referenceOffsets(el.assembly)
  const refOff = offsets[(typeof el.params.locationLine === 'string'
    ? el.params.locationLine : 'wallCenterline') as keyof typeof offsets] ?? 0
  const coreStart = offsets.coreFaceExterior
  const sigma = el.params.flipped ? 1 : -1
  const axisOffset = sigma * (coreStart - refOff + stemW / 2)

  // closed-loop walls (the wallSolid isLoop convention): treat the ring as an open
  // run seamed at node 0 — steps break the ring anyway; the seam is a butt joint
  // (v1 simplification, below grade)
  const p0 = path.nodes[0].pt, pL = path.nodes[path.nodes.length - 1].pt
  const isLoop = !path.closed && path.nodes.length >= 4
    && Math.hypot(p0[0] - pL[0], p0[1] - pL[1]) < 1e-6
  const workPath: Path2D = path.closed ? { closed: false, nodes: [...path.nodes, path.nodes[0]] } : path
  const axisPath = axisOffset === 0
    ? workPath
    : offsetPolyline(isLoop || path.closed
      ? { closed: true, nodes: (isLoop ? path.nodes.slice(0, -1) : path.nodes) }
      : workPath, axisOffset)
  if (!axisPath) return null
  // flattenPath already emits the closing point for a closed path — only append when
  // a ring somehow arrives unclosed
  let axisPts = flattenPath(axisPath)
  if (axisPath.closed && axisPts.length >= 3) {
    const f = axisPts[0], l = axisPts[axisPts.length - 1]
    if (Math.hypot(f[0] - l[0], f[1] - l[1]) > 1e-9) axisPts = [...axisPts, f]
  }
  if (axisPts.length < 2) return null
  const frame = wallFrame(axisPts)
  const L = frame.totalLength
  if (L < EPS) return null

  const zBase = el.level.elevationFt + (num(el.params.baseOffsetFt, 0))
  const stemHeightManual = Math.max(0, num(el.params.stemHeightFt, 2))

  return {
    path, footW, footT, coverage, stepInc, footMat, stemAssembly, stemW, stemMat,
    axisOffset, isLoop, axisPath, axisPts, frame, L, zBase, stemHeightManual,
  }
}

/**
 * STRUCTURAL PLANS (2026-10-21): a footed wall's foundation in PLAN — the stem axis (THE
 * alignment rule above) and the footing / stem widths and footing thickness. What the
 * Foundation Plan dashes and calls out; null for a wall without a foundation.
 */
export function foundationPlanOf(el: ResolvedElement): { axisPts: Pt2[]; footW: number; footT: number; stemW: number } | null {
  const pre = foundationPrelude(el)
  return pre ? { axisPts: pre.axisPts, footW: pre.footW, footT: pre.footT, stemW: pre.stemW } : null
}

/** the live topo-mode grade sampler — grade a hair off BOTH wall faces, high side
 *  governing (CURB round: on a RETAINED wall the graded surface steps at the wall
 *  line, so the two sides differ and the curb rises above the HIGH side; along an
 *  unretained slope both sides agree with the centerline). The centerline sample
 *  rides along for the footing rule. Shared verbatim by the live layout fallback
 *  and sampleTerrainFit — the one-sampler guarantee. */
function liveSamplesAt(
  gradeAt: (pt: Pt2) => number | null, frame: WallFrame, sideOff: number,
): (s: number) => { center: number | null; high: number | null } {
  return (s) => {
    const at = frame.pointAt(s)
    const nrm: Pt2 = [-at.dir[1], at.dir[0]]
    const center = gradeAt(at.pt)
    const left = gradeAt([at.pt[0] + nrm[0] * sideOff, at.pt[1] + nrm[1] * sideOff])
    const right = gradeAt([at.pt[0] - nrm[0] * sideOff, at.pt[1] - nrm[1] * sideOff])
    const highs = [center, left, right].filter((z): z is number => z !== null)
    return { center, high: highs.length ? Math.max(...highs) : null }
  }
}

/** run-scan stations: endpoints + interior polyline vertices + a bounded SAMPLE_FT
 *  grid — shared by the layout's step scan and the sampler's capture grid */
function runStations(pts: Pt2[], L: number): number[] {
  const stations: number[] = [0]
  let acc = 0
  for (let i = 0; i + 1 < pts.length; i++) {
    acc += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
    if (acc > EPS && acc < L - EPS) stations.push(acc)
  }
  const n = Math.min(512, Math.max(1, Math.ceil(L / SAMPLE_FT)))
  for (let i = 1; i < n; i++) stations.push((L * i) / n)
  stations.push(L)
  stations.sort((a, b) => a - b)
  return stations
}

function computeFoundationLayout(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
  /** TERRAIN DECOUPLE round: undefined = normal (the element's own stored fit,
   *  else live); a TerrainFit = use it (the survey's captured/fresh sides);
   *  null = force LIVE sampling regardless of a stored fit */
  fitOverride?: TerrainFit | null,
): FoundationLayout | null {
  const pre = foundationPrelude(el)
  if (!pre) return null
  const {
    footW, footT, coverage, stepInc, footMat, stemAssembly, stemW, stemMat,
    axisOffset, isLoop, axisPath, axisPts, frame, L, zBase, stemHeightManual,
  } = pre

  // ---------- vertical layout: one run (manual) or derived steps (topo) ----------
  let runs: FoundationRun[]
  if (el.params.stemMode === 'topo') {
    // TERRAIN DECOUPLE round: a captured fit reads its stored lanes (no TIN at
    // all); no capture falls back to the live graded surface (pre-round behavior)
    const fit = fitOverride !== undefined ? fitOverride : terrainFitOf(el.params.terrainFit)
    const samplesAt = fit
      ? (s: number): { center: number | null; high: number | null } => {
          const f = L > EPS ? s / L : 0
          return {
            center: terrainFitLane(fit.stations, fit.center, f),
            high: terrainFitLane(fit.stations, fit.high, f),
          }
        }
      : liveSamplesAt(liveGradeReader(byId), frame, stemW / 2 + 0.25)
    // WALL-IS-STEM (2026-08-10): the wall itself is the concrete — no wood to lift
    // above grade, so the curb rule is moot
    const curb = el.params.wallIsStem ? 0 : Math.max(0, num(el.params.curbHeightFt, 0.5))
    // footing top at station s, quantized on the PROJECT-DATUM step grid (multiples
    // of stepInc from project zero), clamped to the wall base — Matt's field report:
    // anchoring the grid at each wall's OWN base put same-type footings in the same
    // grade at different depths whenever base offsets/levels differed. On the shared
    // grid the top is a pure function of (grade, coverage, step) — identical wall to
    // wall. No terrain ⇒ the manual height below the wall base, quantized the same way.
    // FRAMED CRAWL SPACE round (2026-08-16, Matt's a_2yohiSu3Hf report): the stem-top
    // rule splits on where the base sits relative to the terrain AT THIS STATION:
    // - BURIED base (high-side grade at/above the wall base): the TRUE curb regime
    //   (CURB round, unchanged) — the stem quantizes UP past the base so the wood
    //   lands above grade.
    // - Base ABOVE the terrain (the extension-DOWN condition): stepping the stem top
    //   was never a curb question, and the old rule wrongly curb-stepped here too
    //   (grade within curbHeight below the base quantized UP to a full 2ft step).
    //   Default: the stem runs FULL HEIGHT to the base — one tall concrete wall,
    //   only the FOOTING follows grade; a framed floor bearing on it still derives
    //   the mudsill seat below. With Framed Crawl Space ON: the concrete is
    //   minimized — the stem top steps DOWN following grade + the minimum curb
    //   (never below its own footing, never above the base) and the crawl space
    //   above is framed (the wall body + framing extend down to the stepped stem).
    const crawl = !el.params.wallIsStem && el.params.framedCrawlSpace === true
    const topsOf = (s: number): { footingTop: number; stemTop: number } => {
      const g = samplesAt(s)
      const requiredTop = g.center === null ? zBase - stemHeightManual : g.center - coverage
      const footingTop = Math.min(zBase, Math.floor(requiredTop / stepInc + 1e-9) * stepInc)
      let stemTop = zBase
      if (curb > 0 && g.high !== null && g.high >= zBase - 1e-9) {
        stemTop = Math.max(zBase, Math.ceil((g.high + curb) / stepInc - 1e-9) * stepInc)
      } else if (crawl && g.high !== null) {
        stemTop = Math.min(zBase, Math.max(footingTop,
          Math.ceil((g.high + curb) / stepInc - 1e-9) * stepInc))
      }
      return { footingTop, stemTop }
    }
    const sameTops = (a: { footingTop: number; stemTop: number }, b: { footingTop: number; stemTop: number }) =>
      Math.abs(a.footingTop - b.footingTop) < 1e-9 && Math.abs(a.stemTop - b.stemTop) < 1e-9
    // stations: endpoints + axis vertices + a bounded regular sampling
    const stations = runStations(axisPts, L)

    runs = []
    let runStart = 0
    let runTops = topsOf(0)
    for (let i = 1; i < stations.length; i++) {
      const tops = topsOf(stations[i])
      if (sameTops(tops, runTops)) continue
      // bisect the step crossing between the previous station and this one so step
      // edges land on the grade crossing, not the sampling grid. The boundary is
      // `lo` — the last station where the CURRENT run's tops still hold — so the
      // shallower run never extends past where its coverage/curb is proven; the
      // deeper (or taller-curbed) neighbor starts a hair early, always safe.
      let lo = stations[i - 1], hi = stations[i]
      for (let it = 0; it < 12; it++) {
        const mid = (lo + hi) / 2
        if (sameTops(topsOf(mid), runTops)) lo = mid
        else hi = mid
      }
      runs.push({ s0: runStart, s1: lo, ...runTops })
      runStart = lo
      runTops = tops
    }
    runs.push({ s0: runStart, s1: L, ...runTops })
    // absorb slivers into their neighbor — deeper footing + higher stem always keep
    // both coverage and curb
    for (let i = runs.length - 1; i >= 0; i--) {
      if (runs[i].s1 - runs[i].s0 >= MIN_RUN_FT || runs.length === 1) continue
      const into = i > 0 ? runs[i - 1] : runs[i + 1]
      into.footingTop = Math.min(into.footingTop, runs[i].footingTop)
      into.stemTop = Math.max(into.stemTop, runs[i].stemTop)
      if (i > 0) into.s1 = runs[i].s1
      else into.s0 = runs[i].s0
      runs.splice(i, 1)
    }
  } else {
    runs = [{ s0: 0, s1: L, footingTop: zBase - stemHeightManual, stemTop: zBase }]
  }

  // WALL-IS-STEM (2026-08-10, Matt): the wall replaces the stem — collapse every
  // run's stem band to nothing (stemTop = footingTop), which kills the stem prisms,
  // the corner-weld stem component, and any curb in one move; the wall body reads
  // footingTop directly for its stepped base. The footing itself is untouched — the
  // stem TYPE still governs its alignment, exactly "the same footing as would show
  // if an independent stem was provided."
  if (el.params.wallIsStem) {
    for (const r of runs) r.stemTop = r.footingTop
  }

  // FRAMING SESSION Phase 3 — FLOOR BEARING ON THE STEM, derived (doctrine,
  // named for the record: BEARING INTENT IS DECLARED BY MODELED OVERLAP — no
  // toggles). A wood-framed floor whose ring covers the stem AXIS implies
  // bearing there: the stem top derives DOWN to just under the floor framing
  // (the mudsill seat), a single mudsill plate rides on it, and the wall
  // body/framing above start on the floor's sheathing. The opt-out is
  // geometric — model the slab edge to the stem's INNER face and the axis is
  // never covered, so nothing adjusts. Only DOWN (min): a stem already below
  // the seat never rises to meet a floor. wallIsStem walls resolve their
  // bearing at the WALL TOP instead (generate3d) — their collapsed stem runs
  // stay untouched here.
  if (!el.params.wallIsStem && byId) {
    const bands: FloorPlatformBand[] = []
    byId.forEach((cand) => {
      if (cand.id === el.id) return
      const b = floorPlatformBand(cand)
      if (b) bands.push(b)
    })
    if (bands.length) {
      const plateFt = PLATE_THICKNESS_IN / 12
      const bearingAt = (s: number): FloorPlatformBand | null => {
        const pt = frame.pointAt(s).pt
        for (const b of bands) for (const r of b.rings) if (pointInRing(pt, r)) return b
        return null
      }
      const next: FoundationRun[] = []
      for (const run of runs) {
        // scan for coverage transitions on the same grid the step scan uses,
        // bisecting each boundary onto the ring crossing
        const stations: number[] = [run.s0]
        const nS = Math.min(512, Math.max(1, Math.ceil((run.s1 - run.s0) / SAMPLE_FT)))
        for (let i = 1; i < nS; i++) stations.push(run.s0 + ((run.s1 - run.s0) * i) / nS)
        stations.push(run.s1)
        let segStart = run.s0
        let segBand = bearingAt(run.s0 + Math.min(1e-4, (run.s1 - run.s0) / 2))
        /** close the CURRENT segment (governed by segBand) at s1, then switch */
        const closeSeg = (s1: number, nextBand: FloorPlatformBand | null) => {
          if (s1 - segStart >= EPS) {
            const band = segBand
            const seat = band ? band.zJoistBot - plateFt : Infinity
            // engaged only when the stem actually reaches the seat — a stem
            // already below it leaves a modeled gap, not a bearing
            if (band && run.stemTop > seat - 1e-9) {
              next.push({
                ...run, s0: segStart, s1,
                stemTop: Math.min(run.stemTop, seat), floorTop: band.zSheathTop, mudsill: true,
              })
            } else {
              next.push({ ...run, s0: segStart, s1 })
            }
          }
          segStart = s1
          segBand = nextBand
        }
        for (let i = 1; i < stations.length; i++) {
          const b = bearingAt(stations[i])
          if (b === segBand) continue
          // bisect the coverage boundary onto the ring crossing
          let lo = stations[i - 1], hi = stations[i]
          for (let it = 0; it < 12; it++) {
            const mid = (lo + hi) / 2
            if (bearingAt(mid) === segBand) lo = mid
            else hi = mid
          }
          closeSeg(lo, b)
        }
        closeSeg(run.s1, null)
      }
      // absorb slivers (a graze across a ring corner) into their neighbor
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].s1 - next[i].s0 >= MIN_RUN_FT || next.length === 1) continue
        const into = i > 0 ? next[i - 1] : next[i + 1]
        if (i > 0) into.s1 = next[i].s1
        else into.s0 = next[i].s0
        next.splice(i, 1)
      }
      runs = next
    }
  }

  return {
    axisPts, frame, L, runs, axisOffset,
    stemW, footW, footT, stemMat, footMat, zBase, stemAssembly,
    closed: !!axisPath.closed || isLoop,
  }
}

// ---------- TERRAIN DECOUPLE round (docs/bim-plan/19): capture + survey ----------

/** drop consecutive duplicate stations (endpoint/vertex/grid collisions) so the
 *  stored lanes binary-search cleanly */
function dedupStations(stationsFt: number[]): number[] {
  const out: number[] = []
  for (const s of stationsFt) {
    if (!out.length || s - out[out.length - 1] > 1e-9) out.push(s)
  }
  return out
}

/**
 * Sample the LIVE terrain into a captured TerrainFit for this footed wall — the
 * ONE sampler behind the "Update to terrain" action, the doc-open migration, and
 * the survey's fresh side. Foundation lanes (center/high) sample on the SAME
 * station grid and side offsets the live topo-mode layout uses (runStations +
 * liveSamplesAt — shared code, not a copy), so a generate reading the capture is
 * bit-identical to the live generate at capture time. The skirt lane samples the
 * finish skirt's own outer-face line on the WALL frame (buildFinishSkirt's exact
 * offset math). Returns null when the wall isn't footed, has no usable path, or
 * the model has NO topo element at all — an all-null capture would freeze "no
 * terrain" onto a wall drawn before the site arrives; staying live there costs
 * nothing (no TIN exists) and the doc-open migration captures it later.
 */
export function sampleTerrainFit(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
): TerrainFit | null {
  const pre = foundationPrelude(el)
  if (!pre || !byId) return null
  let hasTopo = false
  byId.forEach((e) => { if (e.category === 'topo') hasTopo = true })
  if (!hasTopo) return null
  const gradeAt = liveGradeReader(byId)

  // foundation lanes — the axis frame, the layout's own grid + side offsets
  const samples = liveSamplesAt(gradeAt, pre.frame, pre.stemW / 2 + 0.25)
  const axisStations = dedupStations(runStations(pre.axisPts, pre.L))
  const stations: number[] = []
  const center: (number | null)[] = []
  const high: (number | null)[] = []
  for (const s of axisStations) {
    const g = samples(s)
    stations.push(pre.L > EPS ? s / pre.L : 0)
    center.push(g.center)
    high.push(g.high)
  }

  // skirt lane — the wall frame, the finish skirt's outer-face offset
  // (buildFinishSkirt: offOuter = delta + sign·(t/2), sampled at offOuter + sign·0.25)
  const refLine = flattenPath(pre.path)
  const skirtStations: number[] = []
  const skirt: (number | null)[] = []
  if (refLine.length >= 2) {
    const wFrame = wallFrame(refLine)
    const Lw = wFrame.totalLength
    if (Lw > EPS) {
      const sign: 1 | -1 = el.params.flipped === true ? -1 : 1
      const delta = centerlineOffsetFt(
        el.params.locationLine as LocationLine, el.params.flipped === true, el.assembly)
      const off = delta + sign * (el.assemblyThicknessFt / 2) + sign * 0.25
      for (const s of dedupStations(runStations(refLine, Lw))) {
        const at = wFrame.pointAt(s)
        const nrm: Pt2 = [-at.dir[1], at.dir[0]]
        skirtStations.push(s / Lw)
        skirt.push(gradeAt([at.pt[0] + nrm[0] * off, at.pt[1] + nrm[1] * off]))
      }
    }
  }

  return { v: 1, stations, center, high, skirtStations, skirt }
}

export interface TerrainFitDrift { footing: boolean; skirt: boolean }

/**
 * The on-demand survey diff for ONE captured wall: would "Update to terrain"
 * actually change its geometry? Fresh side goes through the SAME capture pipeline
 * (sampleTerrainFit → lane reads), never a direct TIN read — so immediately after
 * an Update, captured == fresh by construction and the wall reports clean, with
 * no interpolation-vs-TIN knife edges. Quantization does the rest: a grade change
 * too small to move any step/curb/footing top diffs clean. Returns null when the
 * wall is clean or carries no capture.
 */
export function surveyTerrainFit(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
): TerrainFitDrift | null {
  const stored = terrainFitOf(el.params.terrainFit)
  if (!stored) return null
  if (el.category !== 'wall' || !el.params.footingEnabled) return null
  const fresh = sampleTerrainFit(el, byId)

  let footing = false
  if (el.params.stemMode === 'topo') {
    // fresh === null (terrain deleted since capture) → fitOverride null forces the
    // live read, which finds no terrain → the manual-height fallback, correctly
    // diffed against what the capture still produces
    const a = computeFoundationLayout(el, byId, stored)
    const b = computeFoundationLayout(el, byId, fresh)
    footing = !runsMatch(a?.runs ?? null, b?.runs ?? null)
  }

  let skirt = false
  if (el.params.finishToGrade === true && el.params.wallIsStem !== true) {
    const stationsRef = fresh?.skirtStations ?? stored.skirtStations
    for (let i = 0; i < stationsRef.length; i++) {
      const st = stationsRef[i]
      const cv = terrainFitLane(stored.skirtStations, stored.skirt, st)
      const fv = fresh ? (fresh.skirtStations === stationsRef
        ? fresh.skirt[i]
        : terrainFitLane(fresh.skirtStations, fresh.skirt, st)) : null
      if ((cv === null) !== (fv === null)) { skirt = true; break }
      if (cv !== null && fv !== null && Math.abs(cv - fv) > 0.05) { skirt = true; break }
    }
  }

  return footing || skirt ? { footing, skirt } : null
}

function runsMatch(a: FoundationRun[] | null, b: FoundationRun[] | null): boolean {
  if (!a || !b) return a === b
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    // tops are step-grid quantized — exact compare; boundaries carry bisection
    // noise — the sliver tolerance
    if (Math.abs(a[i].footingTop - b[i].footingTop) > 1e-6) return false
    if (Math.abs(a[i].stemTop - b[i].stemTop) > 1e-6) return false
    if (Math.abs(a[i].s0 - b[i].s0) > MIN_RUN_FT) return false
    if (Math.abs(a[i].s1 - b[i].s1) > MIN_RUN_FT) return false
  }
  return true
}

// ---------- prism emission ----------

/** groupKey — shared with the weld pass so a band-matched corner patch joins the
 *  strips' own chunk (union erases the seam) */
const foundationGroupKey = (
  category: 'footing' | 'stem', levelId: string, z0: number, z1: number, materialId: string,
) => `${category}|${levelId}|${z0.toFixed(4)}|${z1.toFixed(4)}|${materialId}`

/** the foundation-axis polyline between stations s0..s1 (endpoints + interior
 *  vertices) — shared with the wall generator's CURB base trim */
export function foundationRunPts(layout: FoundationLayout, s0: number, s1: number): Pt2[] {
  const { axisPts, frame } = layout
  const pts: Pt2[] = [frame.pointAt(s0).pt]
  let acc = 0
  for (let i = 0; i + 1 < axisPts.length; i++) {
    acc += Math.hypot(axisPts[i + 1][0] - axisPts[i][0], axisPts[i + 1][1] - axisPts[i][1])
    if (acc > s0 + EPS && acc < s1 - EPS) pts.push(axisPts[i + 1])
  }
  pts.push(frame.pointAt(s1).pt)
  return pts
}

/**
 * STEM FINISH LAYERS round (2026-08-17, Matt: "make sure all per-layer finish
 * rendering related to stems is resolved"): one band per assembly layer across
 * the run's own width, mirroring `wallLayerBands`' depth-offset math
 * (generate2d.ts) — but working directly off the stem's own derived axis
 * polyline (no stored Path2D exists for a stem run to offset). Layers are
 * ordered exterior→interior using the SAME `flipped`-sign convention the
 * wall's own layers use: THE ALIGNMENT RULE says the stem's exterior face
 * aligns flush with the wall's own exterior core face, so both must agree on
 * which side of the run polyline is "exterior" — `sign = flipped ? -1 : 1`,
 * identical to `wallLayerBands`' own `sign`, applied here to the stem axis
 * instead of the wall's reference path.
 */
function stemLayerBands(
  pts: Pt2[], stemW: number, flipped: boolean, assembly: AssemblyRecord,
): { rings: Pt2[][]; materialId: string }[] {
  if (pts.length < 2 || stemW <= 0) return []
  const path: Path2D = { closed: false, nodes: pts.map((pt) => ({ id: newNodeId(), pt })) }
  const sign = flipped ? -1 : 1
  const boundaryAt = (depth: number): Pt2[] | null => {
    const off = sign * (stemW / 2 - depth)
    const moved = Math.abs(off) < 1e-9 ? path : pathOps.offsetAllEdges(path, off)
    return moved ? moved.nodes.map((n) => n.pt) : null
  }
  const out: { rings: Pt2[][]; materialId: string }[] = []
  let cum = 0
  for (const layer of assembly.layers) {
    const d0 = cum
    cum += layer.thicknessFt
    if (layer.thicknessFt <= 1e-9) continue
    const a = boundaryAt(d0)
    const b = boundaryAt(cum)
    if (!a || !b || a.length < 2 || b.length < 2) continue
    out.push({ rings: [[...a, ...[...b].reverse()]], materialId: layer.materialId })
  }
  return out
}

/**
 * OPENINGS CUT THE STEM (2026-08-10, Matt's garage-door report; z-bounded fix
 * 2026-08-17 — Matt: "doors and windows are having difficulty cutting out of
 * the stems consistently"): each hosted opening's plan cut through the
 * foundation strips — a wide rect along the wall's stored reference path over
 * the placement span (generous half-width; the boolean against the stem's own
 * ring bounds it laterally). The stem is notched between the opening's OWN
 * bottom AND top — foundation below a door threshold, AND above the opening's
 * head (a low window/vent whose head sits below a terrain-following stem's own
 * top, common wherever `stemMode:'topo'` steps the stem taller at one station
 * than another), both stay continuous. The z-bounded root cause: the notch
 * loop below used to cut from `zLow` all the way through the run's own stem
 * top unconditionally, discarding the opening's head entirely — correct only
 * when every opening's head happens to reach the stem top, which is a
 * per-station accident on a sloped/stepped site, not something to rely on.
 */
function foundationVoids(
  el: ResolvedElement,
  hosted: ResolvedElement[],
  halfW: number,
): { rect: Pt2[]; zLow: number; zHigh: number }[] {
  if (!hosted.length || !el.level) return []
  const path = el.params.path as Path2D | null
  if (!path) return []
  const refLine = flattenPath(path)
  if (refLine.length < 2) return []
  const frame = wallFrame(refLine)
  const zBase = el.level.elevationFt + (num(el.params.baseOffsetFt, 0))
  const out: { rect: Pt2[]; zLow: number; zHigh: number }[] = []
  for (const opening of hosted) {
    const placement = placeOpening(opening, frame, zBase)
    if (!placement) continue
    const a = frame.pointAt(placement.s0).pt
    const b = frame.pointAt(placement.s1).pt
    const at = frame.pointAt(placement.center)
    const n: Pt2 = [-at.dir[1], at.dir[0]]
    out.push({
      rect: [
        [a[0] + n[0] * halfW, a[1] + n[1] * halfW],
        [b[0] + n[0] * halfW, b[1] + n[1] * halfW],
        [b[0] - n[0] * halfW, b[1] - n[1] * halfW],
        [a[0] - n[0] * halfW, a[1] - n[1] * halfW],
      ],
      zLow: placement.zLow,
      zHigh: placement.zHead,
    })
  }
  return out
}

export function wallFoundationSolids(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
  hosted: ResolvedElement[] = [],
): ElementSolid[] {
  const layout = foundationLayoutFor(el, byId)
  if (!layout) return []
  const { runs, stemW, footW, footT, stemMat, footMat, stemAssembly } = layout
  const voids = foundationVoids(el, hosted, (el.assemblyThicknessFt || 1) + stemW + footW)
  const stemFlipped = el.params.flipped === true

  const subPts = (s0: number, s1: number): Pt2[] => foundationRunPts(layout, s0, s1)
  const solid = (
    category: 'footing' | 'stem', rings: Pt2[][], z0: number, z1: number, materialId: string,
  ): ElementSolid => ({
    elementId: el.id,
    category,
    outlines: rings,
    zBase: z0,
    zTop: z1,
    materialId,
    // same z band + material ⇒ shared chunk (the column-prism grouping) — abutting
    // footed walls' foundations union their outlines, so corners read continuous
    groupKey: foundationGroupKey(category, el.levelId, z0, z1, materialId),
    pieces: [],
    placements: [],
    openingInstances: [],
    // no plan story this round (the foundation-plan view type is the recorded follow-on)
    skipProjectedOutline: true,
  })

  // FOUNDATION CORNER + STEP round (2026-08-16, part 2 — the spurious step-riser
  // vertical, ROOT CAUSE: a feature-edge suppression gap, NOT earth occlusion. Each
  // step run is its own prism in its own z-band chunk, and prismEdges draws every
  // prism's end verticals over its FULL band — so at each step station both abutting
  // stem prisms drew a full-height vertical where the stem face is geometrically
  // continuous; the line extends ABOVE grade, so no earth boolean could ever have
  // hidden it). Fix: at each run boundary, suppress the verticals over the pair's
  // SHARED (continuous) band only — the exposed riser below/above it keeps its real
  // edge via the new z-RANGED exclusion (edges.ts).
  const stemVertExcl = new Map<number, { p: Pt2; z0: number; z1: number }[]>()
  for (let i = 0; i + 1 < runs.length; i++) {
    const a = runs[i], b = runs[i + 1]
    if (a.stemTop - a.footingTop <= EPS || b.stemTop - b.footingTop <= EPS) continue
    const z0 = Math.max(a.footingTop, b.footingTop)
    const z1 = Math.min(a.stemTop, b.stemTop)
    if (z1 - z0 <= EPS) continue
    const at = layout.frame.pointAt(a.s1)
    const n: Pt2 = [-at.dir[1], at.dir[0]]
    const entries: { p: Pt2; z0: number; z1: number }[] = [
      { p: [at.pt[0] + n[0] * stemW / 2, at.pt[1] + n[1] * stemW / 2], z0, z1 },
      { p: [at.pt[0] - n[0] * stemW / 2, at.pt[1] - n[1] * stemW / 2], z0, z1 },
    ]
    for (const idx of [i, i + 1]) {
      stemVertExcl.set(idx, [...(stemVertExcl.get(idx) ?? []), ...entries])
    }
  }

  const out: ElementSolid[] = []
  for (let ri = 0; ri < runs.length; ri++) {
    const run = runs[ri]
    if (run.s1 - run.s0 < EPS) continue
    const pts = subPts(run.s0, run.s1)
    if (pts.length < 2) continue
    const vertExcl = stemVertExcl.get(ri)
    // STEM FINISH LAYERS round (2026-08-17): when the stem type carries an
    // assembly, emit ONE solid per layer instead of one flat-material solid —
    // each layer's own band (stemLayerBands, mirroring wallLayerBands' depth-
    // offset math) intersected against THIS z-sub-band's own (possibly
    // notched/stepped) rings, so every upstream rule — curb stepping, opening
    // notches, corner-weld z-ranged exclusions — applies identically per layer.
    // A layer band that misses this run in plan (shouldn't happen, but the
    // z-bounded notch merge logic above can hand back an empty intersection at
    // the very ends of a run) is simply skipped rather than emitting an empty
    // solid. Falls back to the flat single-material solid when the stem type
    // has no assembly, or when banding somehow produces nothing at all.
    const stemSolid = (rings: Pt2[][], z0: number, z1: number): ElementSolid[] => {
      if (stemAssembly && stemAssembly.layers.length) {
        const bands = stemLayerBands(pts, stemW, stemFlipped, stemAssembly)
        const perLayer: ElementSolid[] = []
        for (const band of bands) {
          const clipped = intersectClosed(rings, band.rings).filter((r) => r.length >= 3)
          if (!clipped.length) continue
          perLayer.push({
            ...solid('stem', clipped, z0, z1, band.materialId),
            ...(vertExcl ? { excludeVerticalRangesAt: vertExcl } : {}),
          })
        }
        if (perLayer.length) return perLayer
      }
      return [{
        ...solid('stem', rings, z0, z1, stemMat),
        ...(vertExcl ? { excludeVerticalRangesAt: vertExcl } : {}),
      }]
    }
    // CURB round: the stem spans footing top → STEM TOP (zBase where grade allows;
    // stepped above the wall base where the base runs below grade)
    if (run.stemTop - run.footingTop > EPS) {
      const fullRings = inflateOpen(pts, stemW / 2)
      // openings notch the stem strictly BETWEEN their own bottom and head (the
      // z-bounded fix, 2026-08-17) — split the run's stem band at every void's
      // zLow AND zHigh (clamped to this run's own footing/stem-top range), then
      // for each resulting sub-band, subtract only the voids that actually
      // COVER it. A void whose head sits below the run's stem top stops notching
      // there — the stem resumes full-width above it, matching every OTHER
      // z-band in this loop.
      const relevant = voids.filter((v) => v.zLow < run.stemTop - EPS && v.zHigh > run.footingTop + EPS)
      const bounds = [...new Set([
        run.footingTop, run.stemTop,
        ...relevant.flatMap((v) => [
          Math.max(run.footingTop, Math.min(v.zLow, run.stemTop)),
          Math.max(run.footingTop, Math.min(v.zHigh, run.stemTop)),
        ]),
      ])].sort((a, b) => a - b)
      // merge adjacent intervals that resolve to the SAME ring (a void that's
      // z-relevant but misses this run in plan — or two intervals both notched
      // by the identical void set — must stay ONE solid, not fragment into
      // several redundant same-footprint pieces just because a breakpoint
      // landed nearby; the fragment count is a modeling-history accident, not
      // real geometry)
      let openZ0: number | null = null, openRings: Pt2[][] | null = null
      const flush = (z1: number) => {
        if (openZ0 != null && openRings && openRings.length && z1 - openZ0 > EPS) {
          out.push(...stemSolid(openRings, openZ0, z1))
        }
        openZ0 = null; openRings = null
      }
      let prevZ1 = run.footingTop
      for (let bi = 0; bi + 1 < bounds.length; bi++) {
        const z0 = bounds[bi], z1 = bounds[bi + 1]
        if (z1 - z0 <= EPS) continue
        const mid = (z0 + z1) / 2
        const active = relevant.filter((v) => v.zLow <= mid && v.zHigh >= mid)
        let rings = fullRings
        if (active.length) {
          const notched = differenceClosed(rings, active.map((v) => v.rect)).filter((r) => r.length >= 3)
          // a void that misses this run in plan changes nothing — no split seam
          if (areaOf(rings) - areaOf(notched) > 1e-6) rings = notched
        }
        const sameAsOpen = openRings && z0 - prevZ1 <= EPS &&
          areaOf(openRings) === areaOf(rings) && areaOf(differenceClosed(openRings, rings)) < 1e-9
        if (sameAsOpen) {
          prevZ1 = z1
          continue
        }
        flush(z0)
        openZ0 = z0; openRings = rings; prevZ1 = z1
      }
      flush(run.stemTop)
    }
  }
  // FRAMING SESSION Phase 3: the MUDSILL — a single bearing plate riding the
  // derived stem top wherever a wood floor bears on it (run.mudsill; stemTop is
  // already the mudsill seat = joist underside − plate). Emitted into the WALL's
  // framing chunk (`…|framing`) so the systems display treats it as framing,
  // never as foundation concrete; wood material when the wall itself is
  // wood-core, the default (wood-toned) look otherwise.
  for (const run of runs) {
    if (!run.mudsill || run.s1 - run.s0 < EPS) continue
    const pts = subPts(run.s0, run.s1)
    if (pts.length < 2) continue
    const rings = inflateOpen(pts, stemW / 2)
    if (!rings.length) continue
    out.push({
      elementId: el.id,
      category: 'wall',
      outlines: rings,
      zBase: run.stemTop,
      zTop: run.stemTop + PLATE_THICKNESS_IN / 12,
      materialId: isWoodCoreWall(el) ? framingMaterialId(el) : 'default',
      groupKey: `wall|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}|framing`,
      pieces: [],
      placements: [],
      openingInstances: [],
      skipProjectedOutline: true,
      framing: true,
    })
  }

  // footings emit over MAXIMAL same-depth stretches — a stem-top (curb) step alone
  // never joints the footing below it
  const stretches: { s0: number; s1: number; top: number }[] = []
  let f0 = 0
  for (let i = 1; i <= runs.length; i++) {
    if (i < runs.length && Math.abs(runs[i].footingTop - runs[f0].footingTop) < 1e-9) continue
    stretches.push({ s0: runs[f0].s0, s1: runs[i - 1].s1, top: runs[i - 1].footingTop })
    f0 = i
  }

  // FOUNDATION CORNER + STEP round (2026-08-16, part 3 — Matt's construction
  // reality: a stepped footing is poured CONTINUOUS, not butted at the riser): at
  // every step the LOWER footing extends back UNDER the higher one by the step
  // depth (a 1' step down → 1' of lap), capped at the higher stretch's own span.
  // With the firm-standard step increment (2') larger than the footing thickness
  // (10"), the lap slides under with clear z separation — a plain span extension.
  // When a small step increment makes the bands z-OVERLAP (step < thickness), the
  // lap emits as a separate piece CLAMPED to the higher footing's underside — the
  // union volume is identical, nothing interpenetrates or z-fights — and the seam
  // edges where the pieces meet are suppressed (z-ranged verticals + ring
  // segments, edges.ts) so the joint reads as one continuous footing, the same
  // way the corner welds read as one continuous corner.
  const extend0 = stretches.map(() => 0)
  const extend1 = stretches.map(() => 0)
  const footVertPts = stretches.map(() => [] as Pt2[])
  const footVertRanges = stretches.map(() => [] as { p: Pt2; z0: number; z1: number }[])
  const footRingExcl = stretches.map(() => [] as { z: number; a: Pt2; b: Pt2 }[])
  const lapPieces: {
    s0: number; s1: number; z0: number; z1: number
    vertPts: Pt2[]; ringExcl: { z: number; a: Pt2; b: Pt2 }[]
  }[] = []
  for (let j = 0; j + 1 < stretches.length; j++) {
    const a = stretches[j], b = stretches[j + 1]
    const lowerIdx = a.top < b.top ? j : j + 1
    const higherIdx = lowerIdx === j ? j + 1 : j
    const lower = stretches[lowerIdx], higher = stretches[higherIdx]
    const step = higher.top - lower.top
    const lap = Math.min(step, higher.s1 - higher.s0)
    if (lap < EPS) continue
    const station = a.s1                       // the shared step boundary
    const dirSign = lowerIdx === j ? 1 : -1    // lap runs toward the higher stretch
    const hiBot = higher.top - footT
    if (hiBot >= lower.top - 1e-9) {
      // clear z separation — the lower strip simply extends under the higher one;
      // every edge of the extension is a real exposed edge, nothing to suppress
      if (dirSign > 0) extend1[lowerIdx] = lap
      else extend0[lowerIdx] = lap
      continue
    }
    // z-overlap: clamp the lap piece to the higher footing's underside
    const sEnd = station + dirSign * lap
    const atS = layout.frame.pointAt(station)
    const nS: Pt2 = [-atS.dir[1], atS.dir[0]]
    const cornerA: Pt2 = [atS.pt[0] + nS[0] * footW / 2, atS.pt[1] + nS[1] * footW / 2]
    const cornerB: Pt2 = [atS.pt[0] - nS[0] * footW / 2, atS.pt[1] - nS[1] * footW / 2]
    const atE = layout.frame.pointAt(sEnd)
    const nE: Pt2 = [-atE.dir[1], atE.dir[0]]
    const endA: Pt2 = [atE.pt[0] + nE[0] * footW / 2, atE.pt[1] + nE[1] * footW / 2]
    const endB: Pt2 = [atE.pt[0] - nE[0] * footW / 2, atE.pt[1] - nE[1] * footW / 2]
    // the buried seam segments at the higher footing's underside plane: the lap
    // strip's two side runs + the transverse at the station. The transverse at the
    // lap's far END is the real crease where its end face meets the higher
    // footing's underside — never excluded.
    const seamAtHiBot: { z: number; a: Pt2; b: Pt2 }[] = [
      { z: hiBot, a: cornerA, b: endA },
      { z: hiBot, a: cornerB, b: endB },
      { z: hiBot, a: cornerA, b: cornerB },
    ]
    // lower stretch: its end face at the station is fully covered (lap below the
    // underside, higher footing above it) — whole-band vertical suppression; its
    // bottom-ring transverse there is continuous with the lap's underside too
    footVertPts[lowerIdx].push(cornerA, cornerB)
    footRingExcl[lowerIdx].push({ z: lower.top - footT, a: cornerA, b: cornerB })
    // higher stretch: buried against the lower body over [hiBot, lower.top]; the
    // riser above lower.top stays a real edge. Its bottom ring over the lap span
    // lies on the continuous side face — suppressed.
    footVertRanges[higherIdx].push(
      { p: cornerA, z0: hiBot, z1: lower.top },
      { p: cornerB, z0: hiBot, z1: lower.top },
    )
    footRingExcl[higherIdx].push(...seamAtHiBot)
    lapPieces.push({
      s0: Math.min(station, sEnd), s1: Math.max(station, sEnd),
      z0: lower.top - footT, z1: hiBot,
      vertPts: [cornerA, cornerB],
      ringExcl: [...seamAtHiBot, { z: lower.top - footT, a: cornerA, b: cornerB }],
    })
  }

  for (let j = 0; j < stretches.length; j++) {
    const st = stretches[j]
    const s0 = st.s0 - extend0[j], s1 = st.s1 + extend1[j]
    if (s1 - s0 < EPS) continue
    const pts = subPts(s0, s1)
    if (pts.length < 2) continue
    const rings = inflateOpen(pts, footW / 2)
    if (!rings.length) continue
    out.push({
      ...solid('footing', rings, st.top - footT, st.top, footMat),
      ...(footVertPts[j].length ? { excludeVerticalsAt: footVertPts[j] } : {}),
      ...(footVertRanges[j].length ? { excludeVerticalRangesAt: footVertRanges[j] } : {}),
      ...(footRingExcl[j].length ? { excludeRingSegsAt: footRingExcl[j] } : {}),
    })
  }
  for (const lp of lapPieces) {
    if (lp.s1 - lp.s0 < EPS || lp.z1 - lp.z0 < EPS) continue
    const pts = subPts(lp.s0, lp.s1)
    if (pts.length < 2) continue
    const rings = inflateOpen(pts, footW / 2)
    if (!rings.length) continue
    out.push({
      ...solid('footing', rings, lp.z0, lp.z1, footMat),
      excludeVerticalsAt: lp.vertPts,
      excludeRingSegsAt: lp.ringExcl,
    })
  }
  return out
}

// ---------- foundation corner welds (Matt's field report, 2026-08-09) ----------
// Two footed walls whose STORED endpoints coincide clean their wall bodies up via the
// corner-weld patch — but each wall's foundation is its own butt-ended strip along its
// own (offset) axis, so the corner left the same quarter notch unjoined walls used to.
// This pass derives the SAME angle-aware miter patch (cornerWeld.ts's arms/prolongation/
// miter-limit machinery — the arm is generic: end + outward + normal + strip width) for
// the STEM strips and the FOOTING strips independently, over the pair's SHARED z band
// at that end (manual heights or topo steps can differ — only the overlap fills; an
// empty overlap patches nothing). Rules mirror weldWallCornerSolids:
// - exactly-2 footed ends per corner point — T's and 3+ stay butt;
// - the patch is the NOTCH only (minus both strips' own outlines — nothing z-fights);
// - a band+material-matched patch shares the strips' own groupKey (foundationGroupKey),
//   so the chunk union erases the seam; mismatched bands get their own small chunk.

/** the foundation-axis end arm at a wall's stored end (the wallEndArms convention,
 *  offset to the foundation axis, strip width t) */
function foundationEndArm(
  el: ResolvedElement, layout: FoundationLayout, endIdx: 0 | 1, stripW: number,
): CornerArm | null {
  const stored = el.params.path as Path2D | null
  if (!stored?.nodes || stored.nodes.length < 2 || stored.closed || layout.closed) return null
  const nodes = stored.nodes
  const i = endIdx === 0 ? 0 : nodes.length - 1
  const pE = nodes[i].pt
  const pN = nodes[endIdx === 0 ? 1 : nodes.length - 2].pt
  const tv: Pt2 = endIdx === 0 ? [pN[0] - pE[0], pN[1] - pE[1]] : [pE[0] - pN[0], pE[1] - pN[1]]
  const len = Math.hypot(tv[0], tv[1])
  if (len < 1e-9) return null
  const T: Pt2 = [tv[0] / len, tv[1] / len]
  const u: Pt2 = endIdx === 0 ? [-T[0], -T[1]] : T
  const n: Pt2 = [-T[1], T[0]]
  return {
    elementId: el.id,
    el,
    t: stripW,
    // offsetPolyline translates an open end by the end segment's left normal — this
    // IS the foundation axis end point
    end: [pE[0] + n[0] * layout.axisOffset, pE[1] + n[1] * layout.axisOffset],
    u,
    n,
    sign: el.params.flipped === true ? -1 : 1,
  }
}

export function weldFoundationCornerSolids(
  solids: ElementSolid[],
  byId?: Map<string, ResolvedElement>,
): ElementSolid[] {
  if (!clipperReady() || !byId) return []
  // footed walls that actually emitted foundation solids this pass (hidden walls out)
  const footedIds = new Set<string>()
  for (const s of solids) if (s.category === 'footing' || s.category === 'stem') footedIds.add(s.elementId)
  if (footedIds.size < 2) return []

  type EndRec = { pt: Pt2; el: ResolvedElement; layout: FoundationLayout; endIdx: 0 | 1 }
  const ends: EndRec[] = []
  for (const id of footedIds) {
    const el = byId.get(id)
    if (!el) continue
    const layout = foundationLayoutFor(el, byId)
    if (!layout || layout.closed) continue
    const stored = el.params.path as Path2D | null
    if (!stored?.nodes || stored.nodes.length < 2 || stored.closed) continue
    ends.push({ pt: stored.nodes[0].pt, el, layout, endIdx: 0 })
    ends.push({ pt: stored.nodes[stored.nodes.length - 1].pt, el, layout, endIdx: 1 })
  }
  const groups: { pt: Pt2; ends: EndRec[] }[] = []
  for (const e of ends) {
    let g = groups.find((gr) => Math.hypot(gr.pt[0] - e.pt[0], gr.pt[1] - e.pt[1]) < CORNER_TOL)
    if (!g) { g = { pt: e.pt, ends: [] }; groups.push(g) }
    g.ends.push(e)
  }

  // pairs to weld — pass 1: exact stored-endpoint coincidence, the pre-round rule
  // (exactly-2 strict form: T's and 3+ footed ends stay butt). `back` is the extra
  // BACKWARD prolongation the patch strips need: 0 for coincident ends (the strips
  // already reach the corner), positive for segment-drawn pairs whose meet region
  // can sit up to a half body thickness behind a stored end (drawn-past) or in the
  // gap before it (drawn-to-face) — the stock 0.01ft back-extension misses both.
  const pairs: { A: EndRec; B: EndRec; back: number }[] = []
  for (const g of groups) {
    if (g.ends.length !== 2) continue
    const [A, B] = g.ends
    if (A.el.id === B.el.id) continue   // hairpin — same wall meeting itself
    pairs.push({ A, B, back: 0 })
  }

  // pass 2 (FOUNDATION CORNER + STEP round, 2026-08-16 — Matt: "the stems need to
  // clean up exactly the same way, even when the walls are segments, not fully
  // joined walls"): SEGMENT-DRAWN corners. Investigated first per the plan: the
  // wall-body weld has NO general-intersection mechanism to reuse — it is keyed on
  // the same endpoint coincidence; segment-drawn wall BODIES only read clean
  // because their full-thickness bodies physically meet and same-groupKey chunks
  // union per z-band (buildGroupChunk). The narrower, core-face-offset foundation
  // strips stop short of each other at such corners, so they need the weld the
  // bodies never did. Pairing test: two singleton ends of distinct walls whose
  // body-centerline tangents cross NEAR BOTH ENDS — each end within the OTHER
  // wall's half body thickness (+ CORNER_TOL) of the crossing, i.e. the bodies
  // geometrically meet/overlap there (drawn-to-face, drawn-to-centerline, and
  // drawn-slightly-past all qualify; a real gap does not). An end with more than
  // one geometric partner is a 3+ node and stays butt, mirroring the exactly-2
  // rule. The weld emission below is IDENTICAL for both passes — same arms, same
  // patch machinery, no second mechanism.
  const singles = groups.filter((g) => g.ends.length === 1).map((g) => g.ends[0])
  if (singles.length >= 2) {
    const bodyArmOf = (e: EndRec): CornerArm | null => {
      const hit = wallEndArms(e.el)
        .find((x) => Math.hypot(x.pt[0] - e.pt[0], x.pt[1] - e.pt[1]) < 1e-6)
      return hit?.arm ?? null
    }
    const tEff = (e: EndRec) => Math.max(e.el.assemblyThicknessFt, e.layout.stemW, e.layout.footW)
    const cand = new Map<EndRec, EndRec[]>()
    for (let i = 0; i < singles.length; i++) {
      for (let j = i + 1; j < singles.length; j++) {
        const A = singles[i], B = singles[j]
        if (A.el.id === B.el.id) continue
        const armA = bodyArmOf(A), armB = bodyArmOf(B)
        if (!armA || !armB) continue
        const c = armA.u[0] * armB.u[1] - armA.u[1] * armB.u[0]
        if (Math.abs(c) < COLLINEAR_EPS) continue
        const tt = ((armB.end[0] - armA.end[0]) * armB.u[1]
          - (armB.end[1] - armA.end[1]) * armB.u[0]) / c
        const P: Pt2 = [armA.end[0] + armA.u[0] * tt, armA.end[1] + armA.u[1] * tt]
        const reachA = (P[0] - armA.end[0]) * armA.u[0] + (P[1] - armA.end[1]) * armA.u[1]
        const reachB = (P[0] - armB.end[0]) * armB.u[0] + (P[1] - armB.end[1]) * armB.u[1]
        if (Math.abs(reachA) > tEff(B) / 2 + CORNER_TOL) continue
        if (Math.abs(reachB) > tEff(A) / 2 + CORNER_TOL) continue
        cand.set(A, [...(cand.get(A) ?? []), B])
        cand.set(B, [...(cand.get(B) ?? []), A])
      }
    }
    for (const [e, partners] of cand) {
      if (partners.length !== 1) continue
      const other = partners[0]
      if ((cand.get(other) ?? []).length !== 1) continue
      if (e.el.id < other.el.id) {
        pairs.push({ A: e, B: other, back: (tEff(e) + tEff(other)) / 2 + 2 * CORNER_TOL })
      }
    }
  }

  // shift an arm's end BACKWARD along u for the patch derivation only — the patch is
  // the intersection of the two crossing strips (shape-independent of prolongation
  // length once both cover the crossing), so a generous back-shift is safe; seam
  // suppression below keeps using the ORIGINAL arms (the strips' true end corners)
  const backShift = (arm: CornerArm, back: number): CornerArm => back > 0
    ? { ...arm, end: [arm.end[0] - arm.u[0] * back, arm.end[1] - arm.u[1] * back] }
    : arm

  const out: ElementSolid[] = []
  for (const { A, B, back } of pairs) {
    // the end RUN of each wall's layout — its foundation band AT this corner
    const runA = A.endIdx === 0 ? A.layout.runs[0] : A.layout.runs[A.layout.runs.length - 1]
    const runB = B.endIdx === 0 ? B.layout.runs[0] : B.layout.runs[B.layout.runs.length - 1]
    if (!runA || !runB) continue
    const rep = A.el.id < B.el.id ? A : B

    const comps: {
      category: 'footing' | 'stem'
      wA: number; wB: number
      z0: number; z1: number
      mat: string
    }[] = [
      {
        category: 'stem', wA: A.layout.stemW, wB: B.layout.stemW,
        z0: Math.max(runA.footingTop, runB.footingTop),
        // CURB round: the shared band tops out at the LOWER end-run stem top (was
        // the walls' bases — identical when neither end is curbed)
        z1: Math.min(runA.stemTop, runB.stemTop),
        mat: A.layout.stemMat === B.layout.stemMat ? A.layout.stemMat : rep.layout.stemMat,
      },
      {
        category: 'footing', wA: A.layout.footW, wB: B.layout.footW,
        z0: Math.max(runA.footingTop - A.layout.footT, runB.footingTop - B.layout.footT),
        z1: Math.min(runA.footingTop, runB.footingTop),
        mat: A.layout.footMat === B.layout.footMat ? A.layout.footMat : rep.layout.footMat,
      },
    ]
    for (const c of comps) {
      if (c.z1 - c.z0 < EPS || c.wA <= 0 || c.wB <= 0) continue
      const armA = foundationEndArm(A.el, A.layout, A.endIdx, c.wA)
      const armB = foundationEndArm(B.el, B.layout, B.endIdx, c.wB)
      if (!armA || !armB) continue
      const geom = cornerPatchGeom(backShift(armA, back), backShift(armB, back))
      if (!geom) continue   // near-collinear continuation, or no strip overlap
      // the NOTCH only: subtract both walls' own strips (any of their solids in this
      // component whose z band overlaps the patch band), so nothing z-fights
      const bodySolids = solids
        .filter((s) => s.category === c.category
          && (s.elementId === A.el.id || s.elementId === B.el.id)
          && Math.min(s.zTop, c.z1) - Math.max(s.zBase, c.z0) > EPS)
      const bodyRings = bodySolids.flatMap((s) => s.outlines)
      const rings = (bodyRings.length ? differenceClosed(geom.rings, bodyRings) : geom.rings)
        .filter((r) => r.length >= 3)
      if (!rings.length) continue
      // seam-vertical suppression (2026-08-12, mirrors weldWallCornerSolids): when
      // the notch or the strips land in their OWN chunks (band/material mismatch —
      // e.g. topo steps meeting at the corner), prismEdges would draw a vertical at
      // every seam corner where a strip's end face meets the notch. Exclude those
      // points on the notch AND on each wall's own overlapping strips; the notch's
      // far mitered tip is not among them. A band-matched corner shares one chunk
      // whose union already erased these vertices — no-op there, as for walls.
      const seamPts: Pt2[] = [...armFacePts(armA), ...armFacePts(armB), armA.end, armB.end]
      for (const s of bodySolids) {
        const own = s.elementId === A.el.id ? armA : armB
        s.excludeVerticalsAt = [...(s.excludeVerticalsAt ?? []), ...armFacePts(own)]
      }
      out.push({
        elementId: rep.el.id,
        contributorIds: [A.el.id, B.el.id],
        category: c.category,
        outlines: rings,
        zBase: c.z0,
        zTop: c.z1,
        materialId: c.mat,
        // band+material match ⇒ the strips' own key — the chunk union erases the seam
        groupKey: foundationGroupKey(c.category, rep.el.levelId, c.z0, c.z1, c.mat),
        pieces: [],
        placements: [],
        openingInstances: [],
        skipProjectedOutline: true,
        excludeVerticalsAt: seamPts,
      })
    }
  }
  return out
}
